import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  CardMedia,
  MobileStepper,
  Paper,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import vendingImg from '../../assets/vending.jpg';
import atmImage from '../../assets/atm.jpg';
import atmImage2 from '../../assets/atm2.jpg';

import heroImg from "../../assets/hero.jpg";

// Carousel images for vending machines and ATMs
const vendingAtmImages = [
  {
    src: vendingImg,
    alt: "Vending machine in modern office",
  },
  {
    src: atmImage,
    alt: "ATM in urban retail space",
  },
  {
    src: atmImage2,
    alt: "Vending machine in commercial lobby",
  },
];

const carouselSteps = [
  {
    label: "Boost Foot Traffic",
    description:
      "Vending machines and ATMs draw more customers to your business, enhancing visibility and engagement.",
  },
  {
    label: "Elevate Customer Experience",
    description:
      "Offer unmatched convenience with on-site snacks, drinks, and cash access, keeping patrons happy and engaged.",
  },
  {
    label: "Increase On-Site Spending",
    description:
      "With easy access to cash and essentials, customers are more likely to spend generously at your business.",
  },
];

const features = [
  {
    title: "Drive More Visitors",
    description:
      "Strategically placed vending machines and ATMs attract new customers, boosting your business’s appeal.",
    icon: "🚶",
  },
  {
    title: "Seamless Convenience",
    description:
      "Keep customers satisfied with instant access to snacks, drinks, and cash, reducing the need to leave your premises.",
    icon: "🛒",
  },
  {
    title: "Hassle-Free Management",
    description:
      "We handle placement, restocking, and support, letting you focus on running your business.",
    icon: "🤝",
  },
];

const VendingAtmPartnershipPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [vendingStep, setVendingStep] = useState(0);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);
  const handleVendingNext = () => setVendingStep((prev) => prev + 1);
  const handleVendingBack = () => setVendingStep((prev) => prev - 1);

  return (
    <Box sx={{ bgcolor: "#f9fafb", color: "#1a1a1a", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          width: "100%",
          height: { xs: "70vh", sm: "80vh", md: "90vh" },
          background: `url(${heroImg}) center/cover no-repeat`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            px: 4,
            py: 3,
            borderRadius: 2,
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Vending & ATM Partnership
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "#1a1a1a", mt: 1, fontWeight: 500 }}
          >
            Transform your business with our expert solutions
          </Typography>
        </Box>
      </Box>

      {/* Intro Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom align="center">
          Elevate Your Business with Vending & ATMs
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 800, mx: "auto", textAlign: "center" }}
        >
          Partner with us to bring vending machines and ATMs to your property, driving more customers, enhancing convenience, and boosting on-site spending. We handle every detail, so you can focus on growing your business.
        </Typography>
      </Container>

      {/* Carousel Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper
          elevation={3}
          sx={{
            bgcolor: "#fff",
            color: "#1a1a1a",
            p: 4,
            borderRadius: 4,
            mb: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {carouselSteps[activeStep].label}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {carouselSteps[activeStep].description}
          </Typography>
        </Paper>
        <MobileStepper
          steps={carouselSteps.length}
          position="static"
          activeStep={activeStep}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === carouselSteps.length - 1}
              sx={{ color: "#1976d2", fontWeight: 600 }}
            >
              Next
              <KeyboardArrowRight />
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ color: "#1976d2", fontWeight: 600 }}
            >
              <KeyboardArrowLeft />
              Back
            </Button>
          }
          sx={{ bgcolor: "transparent", justifyContent: "center" }}
        />
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="stretch"
        >
          {features.map((feature) => (
            <Card
              key={feature.title}
              sx={{
                flex: 1,
                bgcolor: "#fff",
                color: "#1a1a1a",
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                minHeight: 240,
                transition: "transform 0.3s",
                "&:hover": { transform: "translateY(-4px)" },
              }}
            >
              <Typography variant="h2" sx={{ mb: 2, fontSize: "2.5rem" }}>
                {feature.icon}
              </Typography>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>

      {/* Vending & ATM Carousel */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            bgcolor: "#f9fafb",
          }}
        >
          <CardMedia
            component="img"
            sx={{
              height: { xs: "75vh", sm: "75vh", md: "75vh" },
              objectFit: "cover",
              width: "100%",
            }}
            image={vendingAtmImages[vendingStep].src}
            alt={vendingAtmImages[vendingStep].alt}
          />
        </Card>
        <MobileStepper
          steps={vendingAtmImages.length}
          position="static"
          activeStep={vendingStep}
          nextButton={
            <Button
              size="small"
              onClick={handleVendingNext}
              disabled={vendingStep === vendingAtmImages.length - 1}
              sx={{ color: "#1976d2", fontWeight: 600 }}
            >
              Next
              <KeyboardArrowRight />
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleVendingBack}
              disabled={vendingStep === 0}
              sx={{ color: "#1976d2", fontWeight: 600 }}
            >
              <KeyboardArrowLeft />
              Back
            </Button>
          }
          sx={{ bgcolor: "transparent", justifyContent: "center", mt: 2 }}
        />
      </Container>

      {/* Call to Action */}
      <Container maxWidth="md" sx={{ pb: 10 }}>
        <Paper
          elevation={3}
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            p: 5,
            borderRadius: 4,
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            color: "#1a1a1a",
          }}
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Partner with Confidence
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, maxWidth: 600, mx: "auto", color: "text.secondary" }}
          >
            Ready to enhance your business with vending machines and ATMs? Contact us today to unlock new opportunities for growth and customer satisfaction.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            href="mailto:contact@globalga.co?subject=Vending%20&%20ATM%20Partnership%20Inquiry"
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: "#1976d2",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.3s, box-shadow 0.3s",
              "&:hover": {
                bgcolor: "#1565c0",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            Get Started Today
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default VendingAtmPartnershipPage;